import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import {
  ArrowRight,
  Phone,
  Calculator,
  Sparkles,
  Sun,
  Shield,
  Eye,
  Layers,
  Ruler,
  Building2,
  Truck,
  Wrench,
  Info,
  CheckCircle2,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";
import {
  GuideRelated,
  buildGuideSchemas,
  type GuideMeta,
} from "@/components/GuideLayout";

/**
 * /priser — prissida för solfilm, säkerhetsfilm och dekorfilm.
 *
 * Prisspann som visas här är samma intervall som redan publiceras på
 * /splitterskydd-fonster, /insynsskydd-fonster och /vanliga-fragor — vilket
 * utgör kundens skriftliga bekräftelse. Inga nya/höjda priser introduceras.
 * Kalkylatorn är tydligt markerad som uppskattning, inte offert.
 */

type FilmKey = "standard" | "prestige" | "spegelfilm" | "sakerhet175" | "sakerhet300" | "dekorfilm";

type FilmType = {
  id: FilmKey;
  label: string;
  desc: string;
  low: number; // kr/m² installerat
  high: number;
  icon: typeof Sun;
};

const films: FilmType[] = [
  {
    id: "standard",
    label: "Standard solfilm",
    desc: "Värme- och bländningsskydd, klar eller lätt tonad. Vanligt val för villa & lägenhet.",
    low: 650,
    high: 950,
    icon: Sun,
  },
  {
    id: "prestige",
    label: "3M Prestige (premium klar)",
    desc: "Nanokeramisk premiumfilm. Behåller hela utsikten, kraftig värmereduktion, 15+ års livslängd.",
    low: 950,
    high: 1600,
    icon: Sparkles,
  },
  {
    id: "spegelfilm",
    label: "Spegelfilm / insynsskydd",
    desc: "Envägsspegel-effekt dagtid + värmereduktion. Silver, brons eller neutralgrå.",
    low: 750,
    high: 1600,
    icon: Eye,
  },
  {
    id: "sakerhet175",
    label: "Säkerhetsfilm 175 µm",
    desc: "Standardvalet för splitterskydd & inbrottsfördröjning hos villa och butik.",
    low: 950,
    high: 1400,
    icon: Shield,
  },
  {
    id: "sakerhet300",
    label: "Säkerhetsfilm 300 µm + ramfäste",
    desc: "Högsta nivån — bank, juvelerare, offentlig miljö. EN 12600 klass B/C.",
    low: 1600,
    high: 2200,
    icon: Layers,
  },
  {
    id: "dekorfilm",
    label: "Dekor- / frostfilm",
    desc: "Opak insynsblockering. Glasdörrar, mötesrum, badrum.",
    low: 650,
    high: 1200,
    icon: Layers,
  },
];

const factors = [
  { icon: Ruler, title: "Total fönsteryta (m²)", text: "Ju större fönsteryta du har, desto fler kvadratmeter kan vi fördela de fasta arbetsmomenten på. Därför blir priset per m² lägre. För mindre projekt (under 5 m²) finns ofta en minimidebitering." },
  { icon: Layers, title: "Filmtyp och kvalitet", text: "Du betalar mer för 3M Prestige än för standardfilm och får längre livslängd och bättre prestanda. För säkerhetsfilm ökar priset med tjockleken (100 / 175 / 300 µm)." },
  { icon: Building2, title: "Antal fönster och rumstyp", text: "Vi behöver rengöra, passa in och montera filmen på varje ruta. De momenten tar ungefär lika lång tid oavsett rutans storlek, så många små fönster tar längre tid än några få stora." },
  { icon: Wrench, title: "Tillgänglighet & arbetshöjd", text: "Vi arbetar snabbast i bottenplan där vi kommer åt fönstren fritt. Höga fasader, ställning, lift och möbler eller installationer framför fönstren påverkar hur lång tid jobbet tar." },
  { icon: Truck, title: "Borttagning av gammal film", text: "Behöver vi ta bort gammal solfilm kostar det oftast 150–300 kr/m² extra. Om vi monterar ny film samtidigt gör vi ofta borttagningen utan extra kostnad." },
  { icon: Sparkles, title: "Geografi & projektstorlek", text: "Priset är lägst i Skåne, Halland och Storgöteborg. Har du ett större projekt (>50 m²) får du projektrabatt. Vi anger rabatten i offerten." },
];

const meta: GuideMeta = {
  category: "Priser · Solfilm & säkerhetsfilm",
  title: "Vad kostar solfilm? Priser, prisexempel och kostnadsfri kalkylator",
  tldr:
    "Solfilm i fastighet kostar i Skåne 2026 mellan 650 kr/m² (standardfilm) och 2 200 kr/m² (avancerad säkerhetsfilm med ramfäste) — material och installation inkluderat. Premium klar solfilm (3M Prestige) ligger på 950–1 600 kr/m². Slutpriset beror främst på filmtyp, fönsteryta, antal rutor och tillgänglighet. Använd vår kalkylator för en snabb uppskattning — fast pris får du efter ett kostnadsfritt platsbesök.",
  readTimeMin: 6,
  path: "/priser",
  updated: "2026-06-16",
  faqs: [
    {
      q: "Vad kostar solfilm per kvadratmeter?",
      a: "Skåne 2026, inklusive material, installation och 10 års produktgaranti: standardsolfilm ligger på 650–950 kr/m². Premium klar film (3M Prestige) ligger på 950–1 600 kr/m². Spegelfilm med insynsskydd ligger på 750–1 600 kr/m². Säkerhetsfilm (175 µm) ligger på 950–1 400 kr/m², och avancerad säkerhetsfilm (300 µm + ramfäste) på 1 600–2 200 kr/m². Slutligt pris beror på fönsteryta, antal rutor och tillgänglighet.",
    },
    {
      q: "Vad kostar montering av solfilm?",
      a: "Monteringen ingår alltid i kvadratmeterpriset ovan. Vi delar inte upp priset i film och montage. Du får en samlad kostnad där material, installation, eventuell borttagning av gammal film och resa inom Skåne/Halland/Storgöteborg ingår. Det enda som tillkommer separat är ställning/lift vid extrema arbetshöjder.",
    },
    {
      q: "Vad kostar 3M Prestige 70?",
      a: "3M Prestige 70 (klar premium med 68 % ljustransmission) ligger i prisspannet 1 100–1 600 kr/m² installerat. Exakt pris beror på fönsterytan, antal rutor och arbetshöjd. Modellen är 3M:s flaggskepp för bostäder där man vill behålla full utsikt och naturligt dagsljus.",
    },
    {
      q: "Hur räknar ni ut priset?",
      a: "Vi tar grundpris per m² för vald filmtyp och justerar för: (1) total yta — stora ytor får lägre m²-pris, (2) antal rutor — många små tar längre tid, (3) arbetshöjd och tillgänglighet, (4) eventuell borttagning av gammal film, (5) geografi. Du får alltid en specificerad, skriftlig offert med fast pris innan vi börjar, så du vet exakt vad det kostar.",
    },
    {
      q: "Kostar offerten något?",
      a: "Nej, offerten är kostnadsfri och du behöver inte köpa något. Vi kommer till dig, mäter fönstren och visar filmprover innan du får en skriftlig offert med fast pris. Resan ingår inom Skåne, Halland och Storgöteborg.",
    },
    {
      q: "Hur lång tid tar installationen?",
      a: "En villa med 10–15 m² fönsterglas är klar på en arbetsdag. En större kontorsfastighet (50–100 m²) tar 2–4 dagar. Vi arbetar utan damm eller renoveringsspår, så du kan använda rummen direkt efter att vi gått.",
    },
    {
      q: "Finns det rabatt vid större projekt?",
      a: "Ja, du får projektrabatt när ytan är över 50 m². Vi har också särskilda paketpriser för löpande underhållsavtal med fastighetsbolag. Ange projektets storlek när du ber om offert, så ser du rabatten där.",
    },
  ],
  related: [
    { to: "/solfilm-privat", label: "Solfilm för bostad" },
    { to: "/solfilm-foretag", label: "Solfilm för företag" },
    { to: "/sakerhetsfilm", label: "Säkerhetsfilm" },
    { to: "/3m-prestige", label: "3M Prestige — premium klar solfilm" },
    { to: "/insynsskydd-fonster", label: "Insynsskydd / spegelfilm" },
    { to: "/vanliga-fragor", label: "FAQ — vanliga frågor om solfilm" },
  ],
};

export const Route = createFileRoute("/priser")({
  head: () => ({
    meta: [
      { title: "Solfilm pris — vad kostar fönsterfilm 2026? | BBN Nordic" },
      {
        name: "description",
        content:
          "Solfilm pris 2026: 650–2 200 kr/m² installerat beroende på filmtyp. Prisexempel, vad som påverkar kostnaden och kostnadsfri kalkylator. Fast pris-offert utan köpkrav.",
      },
      { property: "og:title", content: "Solfilm pris — vad kostar fönsterfilm?" },
      {
        property: "og:description",
        content:
          "Prisspann för solfilm, säkerhetsfilm och spegelfilm — 650–2 200 kr/m² installerat. Snabb prisuppskattning med vår kalkylator.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://bbnnordic.se/priser" },
      { property: "og:image", content: IMAGES.hero },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/priser" }],
    scripts: [
      ...buildGuideSchemas(meta),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          name: "BBN Nordic — prislista fönsterfilm 2026",
          url: "https://bbnnordic.se/priser",
          provider: { "@id": "https://bbnnordic.se/#organization" },
          itemListElement: films.map((f) => ({
            "@type": "Offer",
            name: f.label,
            description: f.desc,
            category: "Fönsterfilm — installerat",
            priceCurrency: "SEK",
            priceSpecification: {
              "@type": "UnitPriceSpecification",
              priceCurrency: "SEK",
              price: f.low,
              minPrice: f.low,
              maxPrice: f.high,
              unitText: "kvadratmeter installerat",
              unitCode: "MTK",
              valueAddedTaxIncluded: false,
            },
            availability: "https://schema.org/InStock",
            areaServed: [
              { "@type": "AdministrativeArea", name: "Skåne" },
              { "@type": "AdministrativeArea", name: "Halland" },
              { "@type": "City", name: "Göteborg" },
              { "@type": "Country", name: "Sverige" },
            ],
            seller: { "@id": "https://bbnnordic.se/#organization" },
            priceValidUntil: "2026-12-31",
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function formatKr(n: number) {
  return `${Math.round(n / 100) * 100}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function PriceCalculator() {
  const [sqm, setSqm] = useState(10);
  const [filmId, setFilmId] = useState<FilmKey>("standard");
  const [windows, setWindows] = useState(6);

  const film = films.find((f) => f.id === filmId)!;

  const result = useMemo(() => {
    // Skalfaktor: mindre projekt har högre m²-pris (fasta moment), större projekt lägre.
    const scaleLow = sqm < 5 ? 1.15 : sqm > 40 ? 0.92 : 1;
    const scaleHigh = sqm < 5 ? 1.20 : sqm > 40 ? 0.95 : 1;
    // Många små rutor ger högre arbetstid per m²
    const perWindowSqm = sqm / Math.max(1, windows);
    const fragmentation = perWindowSqm < 1 ? 1.08 : 1;

    const low = sqm * film.low * scaleLow * fragmentation;
    const high = sqm * film.high * scaleHigh * fragmentation;
    return { low, high };
  }, [sqm, film, windows]);

  return (
    <div className="rounded-3xl border border-gold/25 bg-card/70 p-6 md:p-8 shadow-lg">
      <div className="flex items-center gap-3 mb-5">
        <div className="rounded-xl bg-gold/15 p-2.5"><Calculator size={20} className="text-gold" /></div>
        <div>
          <h3 className="font-display text-xl text-foreground">Prisuppskattning</h3>
          <p className="text-xs text-muted-foreground">Riktpris — ersätter inte en skriftlig offert.</p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="flex items-baseline justify-between text-sm font-medium text-foreground mb-2">
            <span>Total fönsteryta</span>
            <span className="text-gold-bright font-semibold">{sqm} m²</span>
          </label>
          <input
            type="range"
            min={2}
            max={120}
            step={1}
            value={sqm}
            onChange={(e) => setSqm(parseInt(e.target.value))}
            className="w-full accent-gold"
            aria-label="Fönsteryta i kvadratmeter"
          />
          <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
            <span>2 m²</span><span>30 m²</span><span>60 m²</span><span>120 m²</span>
          </div>
        </div>

        <div>
          <label className="flex items-baseline justify-between text-sm font-medium text-foreground mb-2">
            <span>Antal fönster / rutor</span>
            <span className="text-gold-bright font-semibold">{windows} st</span>
          </label>
          <input
            type="range"
            min={1}
            max={50}
            step={1}
            value={windows}
            onChange={(e) => setWindows(parseInt(e.target.value))}
            className="w-full accent-gold"
            aria-label="Antal fönster"
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-foreground mb-2">Filmtyp</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            {films.map((f) => {
              const Icon = f.icon;
              const active = filmId === f.id;
              return (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => setFilmId(f.id)}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    active
                      ? "border-gold bg-gold/10"
                      : "border-border bg-card hover:border-gold/40"
                  }`}
                  aria-pressed={active}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={16} className={active ? "text-gold" : "text-muted-foreground"} />
                    <span className="font-medium text-sm text-foreground">{f.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </fieldset>
      </div>

      <div className="mt-6 rounded-2xl bg-navy text-cream p-5 md:p-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold-bright font-semibold mb-2">
          Uppskattat prisspann
        </div>
        <div className="font-display text-3xl md:text-4xl">
          {formatKr(result.low)}–{formatKr(result.high)} kr
        </div>
        <div className="text-sm text-cream/70 mt-2">
          Inkluderar material, installation och 10 års produktgaranti. Exkl. moms vid företagsoffert.
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Link
            to="/offert"
            className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-full font-semibold text-sm min-h-10"
          >
            Få fast pris-offert <ArrowRight size={14} />
          </Link>
          <a
            href="tel:+46771502502"
            className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-5 py-2.5 rounded-full font-medium text-sm min-h-10 hover:bg-gold/10 transition"
          >
            <Phone size={14} /> 0771-502 502
          </a>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
        <Info size={14} className="shrink-0 mt-0.5" />
        <p>
          Detta är en uppskattning baserad på publicerade prisspann för Skåne 2026.
          Faktiskt pris kan avvika ±15 % beroende på arbetshöjd, demontering av gammal
          film, glastyp och projektets totala omfattning. Bindande pris ges efter
          kostnadsfritt platsbesök.
        </p>
      </div>
    </div>
  );
}

function Page() {
  return (
    <main className="bg-background">
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy overflow-hidden">
        <HeroBgImage src={BBN_PHOTOS.buildingSun} alt="Fastighetsfasad med 3M solfilm — offert och prissättning" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
              <Calculator size={14} />
              <span>Priser · Solfilm & säkerhetsfilm</span>
              <span className="text-cream/70">·</span>
              <span className="text-cream/80">{meta.readTimeMin} min läsning</span>
            </div>
            <h1 className="font-display text-cream text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Vad kostar <em className="italic text-gold">solfilm</em>?
            </h1>
            <aside className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 backdrop-blur-sm p-5 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold-bright font-semibold mb-3">
                <Sparkles size={12} /> Kort svar
              </div>
              <p className="text-cream/95 text-base md:text-lg leading-relaxed">
                Solfilm i fastighet kostar i Skåne 2026 mellan
                {" "}<strong className="text-gold-bright">650 kr/m²</strong> (standard solfilm) och
                {" "}<strong className="text-gold-bright">2 200 kr/m²</strong> (avancerad
                säkerhetsfilm med ramfäste) — material och installation inkluderat. Premium
                klar solfilm (3M Prestige) ligger på <strong className="text-gold-bright">950–1 600 kr/m²</strong>.
                Slutpriset styrs av filmtyp, total yta, antal rutor och tillgänglighet.
              </p>
            </aside>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
              <a
                href="#kalkylator"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
              >
                Prova prisuppskattaren <ArrowRight size={16} />
              </a>
              <Link
                to="/offert"
                className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-5 py-3 rounded-full font-medium hover:bg-gold/10 transition min-h-11"
              >
                Få kostnadsfri offert
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <PostHeroConversion />

      {/* Prisspann per filmtyp */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Prisspann per filmtyp</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Riktpriser per kvadratmeter
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl font-light">
              Samtliga priser inkluderar material, installation, 10 års produktgaranti och resa inom Skåne, Halland och Storgöteborg.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {films.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.id} delay={i * 0.05}>
                  <article className="h-full rounded-2xl border border-gold/20 bg-card/60 p-6 hover:border-gold/50 transition-colors">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="rounded-lg bg-gold/15 p-2"><Icon size={18} className="text-gold" /></div>
                      <h3 className="font-display text-lg text-foreground">{f.label}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                    <div className="mt-5 pt-4 border-t border-border">
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Från</div>
                      <div className="font-display text-2xl text-gold-bright">
                        {f.low.toLocaleString("sv-SE")}–{f.high.toLocaleString("sv-SE")} kr/m²
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-8 text-sm text-muted-foreground italic max-w-3xl">
              Det här är våra publicerade prisspann för Skåne 2026, samma som på
              produkt­sidorna. Efter platsbesöket får du en skriftlig offert med fast pris.
              Det är det priset du betalar.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Kalkylator */}
      <section id="kalkylator" className="py-16 md:py-20 bg-card/30 border-y border-border scroll-mt-24">
        <div className="mx-auto max-w-6xl px-5 md:px-10 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <p className="section-number">Räknesnurra</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Snabb prisuppskattning på 30 sekunder
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-light leading-relaxed">
              Ange ungefärlig fönsteryta, antal rutor och filmtyp så får du ett uppskattat
              prisspann direkt. Verktyget använder samma prisstruktur som vi har på våra
              produkt­sidor. Uppskattningen ersätter inte en skriftlig offert.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                "Baserat på faktiska prisspann för Skåne 2026",
                "Räknar med lägre m²-pris för större projekt",
                "Justerar för många små rutor (mer arbetstid per m²)",
                "Visar både den lägre och den högre prisnivån",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-foreground/80">
                  <CheckCircle2 size={16} className="text-gold mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <PriceCalculator />
          </Reveal>
        </div>
      </section>

      {/* Vad påverkar priset */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Pris­drivare</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Vad påverkar priset?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl font-light">
              Här är de sex saker vi tittar på när vi räknar ut priset för dina fönster.
            </p>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {factors.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={i * 0.05}>
                  <article className="h-full rounded-2xl border border-border bg-card/40 p-6">
                    <div className="rounded-lg bg-gold/15 p-2 w-fit"><Icon size={18} className="text-gold" /></div>
                    <h3 className="mt-3 font-display text-lg text-foreground">{f.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prisexempel — typprojekt */}
      <section className="py-16 md:py-20 bg-card/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">Prisexempel</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground">
              Typprojekt — vad andra kunder har betalat
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl font-light">
              Verkliga storleksspann från projekt vi genomfört i Skåne under 2025–2026.
            </p>
          </Reveal>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-gold/15">
            <table className="w-full text-sm">
              <caption className="sr-only">Prisexempel på solfilmsinstallationer 2026.</caption>
              <thead>
                <tr className="bg-navy-light/40 border-b border-gold/15">
                  <th scope="col" className="text-left p-4 font-semibold text-foreground">Projekttyp</th>
                  <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Yta</th>
                  <th scope="col" className="text-left p-4 font-semibold text-muted-foreground">Filmval</th>
                  <th scope="col" className="text-left p-4 font-semibold text-gold-bright">Totalpris</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Lägenhet — balkongdörr + 2 fönster", "≈4 m²", "Spegelfilm", "3 500–6 000 kr"],
                  ["Villa — terrassdörrar + markplan", "≈12 m²", "Säkerhetsfilm 175 µm", "12 000–18 000 kr"],
                  ["Butik — skyltfönster mot gata", "≈20 m²", "Säkerhetsfilm 175 µm", "22 000–32 000 kr"],
                  ["Kontorsfastighet — hela bottenvåningen", "≈80 m²", "Säkerhetsfilm + solfilm", "75 000–115 000 kr"],
                  ["Skola / vårdmiljö — splitterskydd", "≈150 m²", "Säkerhetsfilm 175 µm", "130 000–200 000 kr"],
                ].map(([proj, yta, film, pris], i) => (
                  <tr key={proj} className={i % 2 ? "bg-navy-light/10" : ""}>
                    <th scope="row" className="text-left p-4 font-medium text-foreground">{proj}</th>
                    <td className="p-4 text-muted-foreground">{yta}</td>
                    <td className="p-4 text-muted-foreground">{film}</td>
                    <td className="p-4 text-gold-bright font-semibold">{pris}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground italic">
            Exklusive moms vid företagsoffert. Storleksspann beror på filmval inom kategorin och arbetshöjd.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="section-number mb-3">FAQ</p>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-8">
              Vanliga frågor om pris
            </h2>
            <dl className="space-y-3">
              {meta.faqs?.map((f) => (
                <details key={f.q} className="group bg-card border border-border rounded-xl p-5 open:shadow-md transition-shadow">
                  <summary className="cursor-pointer font-semibold text-foreground flex items-start gap-3 list-none">
                    <span className="mt-0.5 text-gold">›</span>
                    <span className="flex-1">{f.q}</span>
                  </summary>
                  <dd className="mt-3 ml-6 text-foreground/85 leading-relaxed">{f.a}</dd>
                </details>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16 md:py-20 border-y border-gold/20">
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-bright font-semibold mb-5">
            <Shield size={14} /> Kostnadsfri offert · Inget köpkrav
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-cream mb-5">
            Få en kostnadsfri offert anpassad efter ditt projekt
          </h2>
          <p className="text-cream/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Du kan använda riktpriserna när du planerar. För att ge dig ett fast pris behöver vi
            se fönstren. Vi kommer ut, mäter och visar prover, sedan får du en skriftlig offert.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-4 rounded-full font-semibold min-h-12"
            >
              Begär offert <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 border border-gold/40 text-cream px-6 py-4 rounded-full font-semibold hover:bg-gold/10 transition min-h-12"
            >
              <Phone size={16} /> 0771-502 502
            </a>
          </div>
        </div>
      </section>

      <GuideRelated items={meta.related ?? []} />
    </main>
  );
}
